import React, { useEffect, useState } from "react";
import {
    createClient,
    createMicrophoneAndCameraTracks,
    IAgoraRTCRemoteUser,
    AgoraRTCError,
    AgoraRTCErrorCode,
    createMicrophoneAudioTrack
} from "agora-rtc-react";
import { config } from "../../config";
import Controls from "@hubio/videocall-lib/Controls/controls";
import { IJoinArgs, MediaType } from "@interfaces/videocall/videocall";
import Videos from "@hubio/group-videocall-lib/Videos/videos";
import ShareScreen from "@hubio/group-videocall-lib/ShareScreen/shareScreen";
import { ILocalVideoTrack } from "agora-rtc-sdk-ng";

interface IVideocallProps {
    onLeaveChannel?: (...args: unknown[]) => void,
    myUsername: string,
    channelName: string,
    appId: string,
    token: string,
    initialMediaArgs: IJoinArgs
}

const useClient = createClient(config);
const useMicrophoneAndCameraTracks = createMicrophoneAndCameraTracks();
const useMicrophoneTracks = createMicrophoneAudioTrack();
export type videoCallSizeModes = "VideoCall__MinimizedAction" | "VideoCall__FullScreen-Group"

const VideocallGroup = (props: IVideocallProps) => {
    const [userList, setUserLists] = useState<string[]>([]);
    const [users, setUsers] = useState<IAgoraRTCRemoteUser[]>([]);
    const [screenTrack, setScreenTrack] = useState<ILocalVideoTrack>();
    const [start, setStart] = useState<boolean>(false);
    const [screenShare, setScreenShare] = useState<boolean>(false);
    const [audioOnly, setAudioOnly] = useState<boolean>(false);
    const client = useClient();
    const { ready, tracks, error } = useMicrophoneAndCameraTracks();
    const micTracks = useMicrophoneTracks();
    const [showVideo, setShowVideo] = useState(false);
    const [videoCallSizeMode, setVideoCallSizeMode] = useState<videoCallSizeModes>("VideoCall__FullScreen-Group");

    useEffect(() => {
        return () => {
            console.debug("Unload videocall component");
            leaveChannel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // useEffect(() => {
    //     if(users.length === 0) return;
    //     const isRepeated = (new Set(users)).size !== users.length;
    //     if(!isRepeated) return;
    //     // const unique = [...new Map(members.map((m) => [m.id, m])).values()];
    //     // console.log(unique);
    //     const removedDuplicates = [... new Map(users.map((eachUser) => [eachUser.uid, eachUser])).values()]
    //     console.log('removed duplicates', removedDuplicates)
    //     setUsers(removedDuplicates)
    // }, [users])

    useEffect(() => {
        // function to initialise the SDK
        const init = async (name: string) => {
            try
            {
                await client.leave();
            }
            catch(err)
            {
                console.warn("Could not leave client", err);
            }
            client.on("user-published", async (user, mediaType) => {
                await client.subscribe(user, mediaType);
                if (mediaType === "video") {
                    setUsers((prevUsers) => {
                        return [...prevUsers, user];
                    });
                }
                if (mediaType === "audio") {
                    user.audioTrack?.play();
                }
            });

            client.on("user-unpublished", (user, type) => {
                console.log('user unpublished', user);
                if (type === "audio") {
                    user.audioTrack?.stop();
                }
                if (type === "video") {
                    user.videoTrack?.stop();
                    setUsers((prevUsers) => {
                        return prevUsers.filter(
                            (User) => User.uid !== user.uid
                        );
                    });
                }
            });

            client.on("user-joined", user => {
                console.debug("User joined", user);
                setUserLists((prevUsers) => {
                    return [...prevUsers, user.uid.toString()];
                });
            });

            client.on("user-left", (user) => {
                setUsers((prevUsers) => {
                    return prevUsers.filter((User) => User.uid !== user.uid);
                });
                setUserLists((prevUsers) => {
                    return prevUsers.filter(
                        cUser => cUser !== user.uid
                    );
                });
                console.debug("User left", {user, users});
            });

            await client.join(props.appId, name, props.token, props.myUsername);
            if (tracks)
            {
                await client.publish([tracks[0], tracks[1]]);
                await tracks[0].setMuted(false);
                await tracks[0].setEnabled(props.initialMediaArgs.audio);
                await tracks[1].setMuted(false);
                await tracks[1].setEnabled(props.initialMediaArgs.video);
            }
            else if(micTracks.track)
            {                
                await client.publish([micTracks.track]);
                await micTracks.track.setMuted(false);
                await micTracks.track.setEnabled(props.initialMediaArgs.audio);
            }

            setStart(true);
        };

        if (ready && (tracks || micTracks.track)) 
        {
            if(tracks)
            {
                setAudioOnly(false);
                tracks[0].setMuted(!props.initialMediaArgs.audio);
                tracks[1].setMuted(!props.initialMediaArgs.video);
                setShowVideo(props.initialMediaArgs.video);
            }
            else if(micTracks.track)
            {
                setAudioOnly(true);
                micTracks.track.setMuted(!props.initialMediaArgs.audio);
            }
            init(props.channelName)
                .catch(err => handleError(err));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.channelName, client, ready, tracks]);

    useEffect(() => {
        if(error)
            handleError(error);
    }, [error]);

    const handleError = (err: AgoraRTCError) => {
        console.error("Agora error", err);
        if(!err?.code) return;
        switch(err.code)
        {
            case AgoraRTCErrorCode.INVALID_OPERATION:
                console.warn("Invalid operation");

                break;
            default:
                break;
        }
    }

    const mute = async (type: MediaType, enable: boolean) => {
        if(!(tracks && micTracks.track)) return;
        if (type === "audio") 
        {
            if(tracks)
            {
                await tracks[0].setMuted(false);
                await tracks[0].setEnabled(enable);
                return;
            }
            await micTracks.track.setMuted(false);
            await micTracks.track.setEnabled(enable);
        } 
        else if (type === "video") 
        {
            // await tracks[1].setMuted(false);
            await tracks[1].setEnabled(enable);
            setShowVideo(enable);
        }
        else if (type === "screen")
        {
            setScreenShare(enable);
        }
    };

    const leaveChannel = async () => {
        // we close the tracks to perform cleanup
        if(tracks)
        {
            tracks.forEach(track => track.close());
        }

        if(micTracks.track)
        {
            micTracks.track.close();
        }
    
        try
        {
            if(client.connectionState === "CONNECTED")
            {
                await client.leave();
                client.removeAllListeners();
            }
        }
        catch(err)
        {
            console.warn("Could not close client");
        }
        // the camera light should go off
        setStart(false);
        // you can now unmount the component if required

        if(props.onLeaveChannel)
            props.onLeaveChannel();
    };

    const handleVideoSizeMode = () => {
        switch (videoCallSizeMode)
        {
            case "VideoCall__MinimizedAction":
                setVideoCallSizeMode("VideoCall__FullScreen-Group");
                break;
            case "VideoCall__FullScreen-Group":
                setVideoCallSizeMode("VideoCall__MinimizedAction");
                break;
            default:
                break;
        }
    }

    const screenPublish = async (publish: boolean, track: ILocalVideoTrack, callback?: () => void) => {
        if(!(start && client)) return;
        if(publish)
        {
            if(tracks && tracks.length > 0)
                await client.unpublish(tracks[1]);
            await client.publish(track);
            setScreenTrack(track);
        }
        else
        {
            try
            {
                await client.unpublish(track);
                track.close();
                track.removeAllListeners();
            }
            catch(error)
            {
                console.warn("Screen publish track error", error);
            }
            setScreenTrack(undefined);
            setScreenShare(false);
            if(tracks && tracks.length > 0)
            {
                let hasUnmute = false;
                if(!tracks[1].enabled) 
                {
                    hasUnmute = true;
                    await tracks[0].setEnabled(true);
                    await tracks[1].setEnabled(true);
                    await tracks[1].setMuted(true);
                }
                if(tracks[1] && tracks[1].enabled)
                {
                    console.debug("Publishing tracks");
                    await client.publish(tracks);
                }
                else
                    console.debug("Tracks is not enabled", {tracks, ti: tracks[0]});
                
                if(hasUnmute)
                {
                    await tracks[1].setMuted(false);
                    await tracks[1].setEnabled(false);
                }
            }
        }
    }

    return (
        <div className="Chat">
            <div className="Chat__Card">
                <div className="CardLayout-InsideCentered">
                    <div className={videoCallSizeMode} onClick={() => videoCallSizeMode === "VideoCall__MinimizedAction" ?  handleVideoSizeMode() : null}>
                        <em className="fas fa-chevron-right"/>
                        <div className="VideoCall__Video">
                            <div className="VideoCall__TopBar" style={{zIndex: "1"}} >
                                <em className="fas fa-minus" onClick={() => handleVideoSizeMode() }/>
                            </div>
                                {/* User videoplayer */}
                            {/* <div className="VideoCall__MiniInfo">
                                <div className="VideoCall__MiniInfoVideo" style={{zIndex: "5"}} >
                                    {
                                        start && tracks && tracks.length > 0 && showVideo ?
                                        <AgoraVideoPlayer
                                            className="vid"
                                            videoTrack={tracks[1]}
                                            style={{ height: "100%", width: "100%" }}
                                        />
                                        :
                                        <div className="VideoCall__Info">
                                            <div className="VideoCall__Avatar">
                                                <Avatar  
                                                    username={props.myUsername}
                                                    roleCode="PR"
                                                    size={30}
                                                    editable={false}
                                                />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div> */}

                            {/* Users players */}
                            <>
                                {
                                    (tracks || (users && users.length > 0)) ?
                                        <>
                                            <Videos
                                                userList={userList}
                                                users={users}
                                                tracks={tracks} 
                                                start={start}
                                                myUsername={props.myUsername}
                                                showVideo={showVideo}
                                                shareScreen={screenShare}
                                                screenTrack={screenTrack}
                                            />

                                            {
                                                screenShare &&
                                                    <ShareScreen
                                                        publish={(track) => screenPublish(true, track)} 
                                                        unpublish={(track) => screenPublish(false, track)} />
                                            }
                                        </>
                                    :
                                    <>
                                        <span>... Something</span>
                                    </>
                                }
                            </>

                            <Controls
                                initialValues={props.initialMediaArgs}
                                onMute={mute}
                                onLeave={leaveChannel}
                                audioOnly={audioOnly}
                                canShare={true}
                                isSharingScreen={screenTrack !== undefined} 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VideocallGroup;
